/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Personalize a message", description = "Insert warehouse data as variables, set fallbacks, transform values, and write Liquid directly.", lastUpdatedDate = new Date(1786492800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Personalize a message",
        description: "Insert warehouse data as variables, set fallbacks, transform values, and write Liquid directly.",
        lastUpdatedDate: "Wed Aug 12 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Insert a variable",
          "depth": 0,
          "id": "insert-a-variable"
        }, {
          "value": "Set a fallback value",
          "depth": 0,
          "id": "set-a-fallback-value"
        }, {
          "value": "Transform a value",
          "depth": 0,
          "id": "transform-a-value"
        }, {
          "value": "Write Liquid directly",
          "depth": 0,
          "id": "write-liquid-directly"
        }, {
          "value": "What happens when data is missing",
          "depth": 0,
          "id": "what-happens-when-data-is-missing"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      h2: "h2",
      ol: "ol",
      li: "li",
      code: "code",
      pre: "pre",
      ul: "ul",
      a: "a"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, lifecycle and CRM teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: "An email message with content"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Personalization inserts each recipient's own data into the email. Write variables directly into your content, or use logic to build conditional statements and calculations. Hightouch fills them from your warehouse at send time."
        })
      }), "\n", _jsx(_components.h2, {
        id: "insert-a-variable",
        children: "Insert a variable"
      }), "\n", _jsx(_components.p, {
        children: "Where the control lives depends on the editor."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Editor"
            }), _jsx(_components.th, {
              children: "How to insert"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Email, HTML"
            }), _jsxs(_components.td, {
              children: ["Select ", _jsx(_components.strong, {
                children: "Add variable"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Email, drag-and-drop"
            }), _jsx(_components.td, {
              children: "Use the merge tag control on the text block you're editing"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "SMS"
            }), _jsxs(_components.td, {
              children: ["Select ", _jsx(_components.strong, {
                children: "Insert variable"
              }), " in the ", _jsx(_components.strong, {
                children: "Message"
              }), " or ", _jsx(_components.strong, {
                children: "Link"
              }), " field"]
            })]
          })]
        })]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Search for the attribute you want and select it."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Insert variable"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The variable is written as Liquid, using the attribute name: ", _jsx(_components.code, {
          children: "{{ first_name }}"
        }), ". Variables appear in your content as pills you can click to edit, swap, or remove."]
      }), "\n", _jsx(_components.p, {
        children: "Attributes are grouped by where they come from:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Group"
            }), _jsx(_components.th, {
              children: "What it holds"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Properties"
              })
            }), _jsx(_components.td, {
              children: "Columns on the profile, such as first name or loyalty tier"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Entry event"
              })
            }), _jsx(_components.td, {
              children: "Properties of the event that triggered a journey"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Relations"
              })
            }), _jsx(_components.td, {
              children: "Data from related models, such as recent orders"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Traits"
              })
            }), _jsx(_components.td, {
              children: "Computed traits, such as lifetime order count"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Message context"
              })
            }), _jsx(_components.td, {
              children: "Details of the send itself, such as the subscription topic name"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Dates"
              })
            }), _jsx(_components.td, {
              children: "Date values you can format or offset"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "set-a-fallback-value",
        children: "Set a fallback value"
      }), "\n", _jsx(_components.p, {
        children: "An attribute with no value renders as nothing, which leaves a gap in your content. Give every variable a fallback so the sentence still reads."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the attribute, then select ", _jsx(_components.strong, {
            children: "Add transformation"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Choose ", _jsx(_components.strong, {
            children: "Default"
          }), " and enter the value to use when the attribute is empty."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Insert variable"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The result is ", _jsx(_components.code, {
          children: "{{ first_name | default: \"there\" }}"
        }), ", which renders \"Hello there\" for a recipient with no first name."]
      }), "\n", _jsxs(_components.p, {
        children: ["In the SMS editor you can also select a variable in the preview and set its ", _jsx(_components.strong, {
          children: "Fallback value"
        }), " there. A fallback changes the character count. A short name in the preview and a long fallback in real sends can push a message into a second part."]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Use the same fallback everywhere a given attribute appears. Two different fallbacks for one attribute is an error that blocks the send."
        })
      }), "\n", _jsx(_components.h2, {
        id: "transform-a-value",
        children: "Transform a value"
      }), "\n", _jsxs(_components.p, {
        children: ["Transformations reshape a value before it renders: format a date, round a number, change case, or truncate text. Select ", _jsx(_components.strong, {
          children: "Add transformation"
        }), " after choosing an attribute, then check the result in the transformation preview before inserting."]
      }), "\n", _jsxs(_components.p, {
        children: ["Transformations are grouped as ", _jsx(_components.strong, {
          children: "Text"
        }), ", ", _jsx(_components.strong, {
          children: "Numbers"
        }), ", ", _jsx(_components.strong, {
          children: "Arrays"
        }), ", and ", _jsx(_components.strong, {
          children: "Dates"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "write-liquid-directly",
        children: "Write Liquid directly"
      }), "\n", _jsx(_components.p, {
        children: "Native Delivery renders content with Liquid. Write it by hand in the HTML editor for logic the picker doesn't cover, including conditionals and loops:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "{% if loyalty_tier == \"gold\" %}\n  <p>You have {{ points }} points to spend.</p>\n{% else %}\n  <p>Join our loyalty program.</p>\n{% endif %}\n"
        })
      }), "\n", _jsx(_components.p, {
        children: "Conditionals and loops must be hand-written today. The picker covers attribute insertion, fallbacks, and transformations."
      }), "\n", _jsx(_components.h2, {
        id: "what-happens-when-data-is-missing",
        children: "What happens when data is missing"
      }), "\n", _jsx(_components.p, {
        children: "Two cases behave differently:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Case"
            }), _jsx(_components.th, {
              children: "Result"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "The attribute exists but is empty for this recipient"
            }), _jsx(_components.td, {
              children: "The message sends. The variable renders as nothing."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "The attribute name doesn't match a column"
            }), _jsx(_components.td, {
              children: "The send fails for the whole batch of recipients."
            })]
          })]
        })]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "A name that matches no column fails the send for a whole batch of recipients, not just the one it affects. An empty value is fine and renders blank. A misspelling counts as a misconfiguration, not missing data. Check the composer for variable errors before you activate a campaign."
        })
      }), "\n", _jsx(_components.p, {
        children: "The composer flags unmatched attribute names while you edit. An empty value for the profile you happen to be previewing is not flagged, because a blank cell on one profile is normal."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["At send time, an unrecognized filter or variable renders as nothing, so a recipient never sees a raw ", _jsx(_components.code, {
            children: "{{ placeholder }}"
          }), ". The composer is where to catch these."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/preview-and-test",
            children: "Preview and test"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/native-delivery/campaigns",
            children: "Send a campaign"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
